import { getTechnologyIconName, starUnHighlightedIcon } from '../../../../styles';
import {
  ActionCellRenderer,
  ActionTypes,
} from '../../../../utils/custom-renderer/action-cell-renderer';
import type { SellerInterest } from './seller-interest';
import { ICONS, VPPA_RFQ_STATUS } from '../../../../utils';
import { getAbbreviationForCreditProfile } from '../../../../utils/credit-profiles';
import {
  HyperlinkStaticCellRenderer,
  IGridHyperlink,
} from '../../../../utils/custom-renderer/hyperlink-cell-renderer';
import {
  getNumericPattern,
  formatMMDDYYYY,
  getStatusTranslate,
  getNumericDecimalPattern,
  getFillColorFromContractType,
} from '../../../../utils/formatting';
import {
  CUSTOM_HEADER_TOOLTIPS,
  getGroupColumnSameObject,
  headerWithTooltipColParams,
} from '../../../../utils/grid';
import { CASCADE_ENUM } from '../../../../services/enum.service';

function getStatusStyle(params: any, context) {
  const response = {};

  if (params.data?.VPPA_STATUS === VPPA_RFQ_STATUS.ACTIVE) {
    response['color'] = '#7acc79';
  }

  if (params.data?.VPPA_STATUS === VPPA_RFQ_STATUS.EXISTING_INTEREST) {
    response['color'] = 'rgba(255, 182, 96, 0.99)';
  }

  return response;
}

export enum COLUMN_FIELDS {
  TICKER = 'TICKER',
  UNIQUE_ID = 'UNIQUE_ID',
  PROJECT_NAME = 'NAME',
  ISO = 'ISO',
  SETTLEMENT_POINT = 'SETTLEMENT_POINT',
  OFFERED_CAPACITY = 'OFFERED_CAPACITY',
  PRICE = 'FIXED_PRICE',
  TENOR_START_DATE = 'TENOR_START_DATE',
  TENOR_MONTHS = 'TENOR_MONTHS',
  COLLATERAL = 'SELLER_POST_COD_COLLATERAL',
  IMPLIED_CARBON_PRICE = 'IMPLIED_CARBON_PRICE',
  LME = 'LME',
  VPPA_STATUS = 'VPPA_STATUS',
  VPPA_OBJECT = 'VPPA_OBJECT',
  ACTIONS = 'ACTIONS',
  APPLICABLE_REC_PROGRAM = 'APPLICABLE_REC_PROGRAM',
  TECHNOLOGY = 'TECHNOLOGY',
}

export function VPPA_COLUMNS(context: SellerInterest) {
  return [
    {
      headerName: 'Type',
      suppressMenu: true,
      maxWidth: 62,
      valueGetter: (params) => {
        const iconList: Array<{ icon: ICONS; width; height; fill? }> = [];
        if (params.data) {
          iconList.push({
            icon: getTechnologyIconName(params.data.TECHNOLOGY),
            width: '15px',
            height: '18px',
            fill: getFillColorFromContractType(params.data.CONTRACT_TYPE),
          });

          return JSON.stringify({
            NAME: ' ',
            ICON_LIST: iconList,
          });
        }
      },
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        if (!params.value && !params.data) return;

        const obj = JSON.parse(params.value);
        const hyperlinkParams: IGridHyperlink = {
          value: obj?.NAME,
          id: '',
          vppaId: null,
          iconList: obj?.ICON_LIST,
        };

        return hyperlinkParams;
      },
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.ICON_LEGEND),
    },
    {
      field: COLUMN_FIELDS.TICKER,
      headerName: 'Ticker',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.UNIQUE_ID,
      headerName: 'Unique ID',
      maxWidth: 70,
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.PROJECT_NAME,
      headerName: 'Project',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      comparator: (valueA, valueB, nodeA, nodeB) => {
        if (valueA == valueB) return 0;
        return valueA.toLowerCase() > valueB.toLowerCase() ? 1 : -1;
      },
      valueGetter: (params) => {
        const iconList: Array<{ icon: ICONS; width; height }> = [];
        if (params.data) {
          return JSON.stringify({
            NAME: params.data.NAME,
            PROJECT_ID: params.data.PROJECT_ID,
          });
        }
      },
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        if (!params.value && !params.data) return;

        const obj = JSON.parse(params.value);
        const hyperlinkParams: IGridHyperlink = {
          value: obj?.NAME,
          id: obj?.PROJECT_ID,
          vppaId: null,
          iconList: [],
        };

        return hyperlinkParams;
      },
    },

    {
      field: COLUMN_FIELDS.ISO,
      headerName: 'ISO',
      maxWidth: 90,
      sortable: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.SETTLEMENT_POINT,
      headerName: 'Settlement Point',
      maxWidth: 100,
      sortable: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.OFFERED_CAPACITY,
      headerName: 'Offered Capacity (MW)',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.PRICE,
      headerName: 'Fixed Price',
      maxWidth: 85,
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      valueFormatter: (params) => getNumericPattern(params.data.FIXED_PRICE, '', '$'),
    },
    {
      field: COLUMN_FIELDS.TENOR_START_DATE,
      headerName: 'Start Date',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      maxWidth: 85,
      flex: 1,
      valueFormatter: (params) =>
        params.data
          ? formatMMDDYYYY(params.data.TENOR_START_DATE)
          : formatMMDDYYYY(
              getGroupColumnSameObject(params, COLUMN_FIELDS.VPPA_OBJECT).TENOR_START_DATE,
            ),
    },

    {
      field: COLUMN_FIELDS.TENOR_MONTHS,
      headerName: 'Tenor (mo)',
      maxWidth: 71,
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      valueFormatter: (params) =>
        params.data
          ? params.data.TENOR_MONTHS
          : getGroupColumnSameObject(params, COLUMN_FIELDS.VPPA_OBJECT).TENOR_MONTHS,
    },
    {
      field: COLUMN_FIELDS.COLLATERAL,
      headerName: 'Credit Profile ($/MW)',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      valueFormatter: (params) => {
        let collateral = '';
        if (params.data) {
          collateral = getNumericPattern(
            params.data.SELLER_POST_COD_COLLATERAL,
            getAbbreviationForCreditProfile(params.data.VPPA_CREDIT_PROFILE),
            '$',
          );
        } else {
          collateral = getNumericPattern(
            getGroupColumnSameObject(params, COLUMN_FIELDS.VPPA_OBJECT).SELLER_POST_COD_COLLATERAL,
            getAbbreviationForCreditProfile(
              getGroupColumnSameObject(params, COLUMN_FIELDS.VPPA_OBJECT).VPPA_CREDIT_PROFILE,
            ),
            '$',
          );
        }
        return collateral;
      },
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.CREDIT_PROFILE),
    },
    {
      field: COLUMN_FIELDS.LME,
      headerName: 'LME',
      maxWidth: 70,
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      valueFormatter: (params) => getNumericDecimalPattern(params.data.LME, null, ''),
    },
    {
      field: COLUMN_FIELDS.IMPLIED_CARBON_PRICE,
      headerName: 'Implied Carbon Price',
      sortable: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      flex: 1,
      valueFormatter: (params) =>
        getNumericDecimalPattern(params.data.IMPLIED_CARBON_PRICE, null, '$'),
    },
    {
      field: COLUMN_FIELDS.VPPA_STATUS,
      headerName: 'Status',
      maxWidth: 65,
      sortable: true,
      filter: 'agSetColumnFilter',
      menuTabs: ['filterMenuTab'],
      cellStyle: (params) => getStatusStyle(params, context),
      valueFormatter: (params) => getStatusTranslate(params.data.VPPA_STATUS),
      filterValueGetter: (params) => getStatusTranslate(params.data.VPPA_STATUS),
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.APPLICABLE_REC_PROGRAM,
      headerName: 'Applicable REC Program',
      sortable: true,
      filter: 'agSetColumnFilter',
      filterValueGetter: (params) => context.enumService.getEnumTranslation(
          CASCADE_ENUM.APPLICABLE_REC_PROGRAM,
          params.data.APPLICABLE_REC_PROGRAM,
      ),
      menuTabs: ['filterMenuTab'],
      flex: 2,
      valueFormatter: (params) => {
        if (params.data) {
          return context.enumService.getEnumTranslation(
            CASCADE_ENUM.APPLICABLE_REC_PROGRAM,
            params.data.APPLICABLE_REC_PROGRAM,
          );
        } else {
          return context.enumService.getEnumTranslation(
            CASCADE_ENUM.APPLICABLE_REC_PROGRAM,
            getGroupColumnSameObject(params, COLUMN_FIELDS.VPPA_OBJECT).APPLICABLE_REC_PROGRAM,
          );
        }
      },
    },
    {
      field: COLUMN_FIELDS.ACTIONS,
      headerName: '',
      suppressMenu: true,
      maxWidth: 50,
      pinned: 'right',
      cellStyle: { 'padding-right': '7px', 'box-shadow': '-5px 0px 15px 0px rgba(0, 0, 0, 0.2)' },
      cellRenderer: ActionCellRenderer,
      cellRendererParams: (params) => {
        if (!params.data) {
          return;
        }
        const sellerInterestBtn = {
          name: starUnHighlightedIcon(),
          callback: (detail) => {
            context.$emit('favoriteSellerInterestVppa', detail);
          },
          type: ActionTypes.FAVORITE,
          actionName: 'favorite',
          tooltip: 'Add to Watchlist',
        };
        const actions = [sellerInterestBtn];
        const enabled = true;
        return {
          actions,
          enabled,
        };
      },
    },
  ];
}
